import { useSelector } from "react-redux";

/**
 * Hook to access notification counts from the chat store
 *
 * Usage examples:
 *
 * // Get global notification count
 * const { global } = useNotificationCounts();
 *
 * // Get firm-specific notification count
 * const { firm } = useNotificationCounts({ firmId: 123 });
 *
 * // Get thread-specific notification count
 * const { thread } = useNotificationCounts({ threadId: "abc-123" });
 *
 * // Get multiple counts at once
 * const { global, firm, thread } = useNotificationCounts({
 *   firmId: 123,
 *   threadId: "abc-123"
 * });
 */
export const useNotificationCounts = (options = {}) => {
  const { firmId, threadId } = options;

  const notificationCounts = useSelector((state) => state.chat2.notificationCounts);

  const globalCount = notificationCounts?.global?.unread ?? 0;
  const firmCount = firmId && notificationCounts.firm ? notificationCounts.firm[firmId] || 0 : 0;
  const allFirmCount = notificationCounts?.firm ?? 0;
  const threadCount =
    threadId && notificationCounts?.thread ? notificationCounts?.thread[threadId] || 0 : 0;
  const allThreads = notificationCounts?.thread ?? 0;
  return {
    global: globalCount ?? 0,
    firm: firmCount ?? 0,
    thread: threadCount ?? 0,
    allFirmCount: allFirmCount ?? {},
    allThreads: allThreads ?? {},
    hasGlobal: globalCount > 0,
    hasFirm: firmCount > 0,
    hasThread: threadCount > 0,
    hasAny: globalCount > 0 || firmCount > 0 || threadCount > 0,
    globalFormatted: globalCount > 99 ? "99+" : globalCount.toString(),
    firmFormatted: firmCount > 99 ? "99+" : firmCount.toString(),
    threadFormatted: threadCount > 99 ? "99+" : threadCount.toString(),
  };
};

/**
 * Hook to get all notification data (for debugging or advanced usage)
 */
export const useAllNotificationCounts = () => {
  const notificationCounts = useSelector((state) => state.chat2.notificationCounts);

  return {
    ...notificationCounts,
    getAllFirmCounts: () => notificationCounts.firm || {},
    getAllThreadCounts: () => notificationCounts.thread || {},
    getTotalUnread: () => {
      const global = notificationCounts.global?.unread || 0;
      const firmCounts = Object.values(notificationCounts.firm || {});
      const threadCounts = Object.values(notificationCounts.thread || {});

      return (
        global +
        firmCounts.reduce((sum, count) => sum + count, 0) +
        threadCounts.reduce((sum, count) => sum + count, 0)
      );
    },
  };
};

export default useNotificationCounts;
